import { useMutation } from "@tanstack/react-query";
import { createParticipantsLight } from "api/services/user-service";
import { ICreateParticipantsLightRequest } from "api/models/request/ICreateParticipantsLightRequest";
import { useAuthContext } from "./useAuthContext";
import useRefreshToken from "hooks/useRefreshToken";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { RoutePaths } from "constants/RoutePaths";

export const useCreateParticipantsLight = () => {
    const { userState } = useAuthContext();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { refreshToken } = useRefreshToken();

    const mutation = useMutation({
        mutationFn: async (request: ICreateParticipantsLightRequest): Promise<string[]> => {
            const workspaceId = userState.user.selectedWorkspace.workspaceId;

            let response;
            try {
                response = await createParticipantsLight(request, workspaceId);
            } catch (e: any) {
                if (e?.response?.status !== 401) {
                    throw e;
                }

                const refreshResponse = await refreshToken();
                if (!refreshResponse.success) {
                    window.location.href = RoutePaths.LOGIN;
                    throw e;
                }

                response = await createParticipantsLight(request, workspaceId);
            }

            if (!response?.success) {
                throw new Error(response?.message ?? 'createParticipantsLight.error');
            }

            return response.data ?? [];
        },
        onSuccess: () => {
            enqueueSnackbar(t("createParticipantsLight.success"), { variant: "success" });
        },
        onError: () => {
            enqueueSnackbar(t("createParticipantsLight.error"), { variant: "error" });
        },
    });

    return {
        createParticipantsLight: mutation.mutateAsync,
        creatingParticipantsLight: mutation.isPending,
    };
};
